import React, { useState } from "react";
import { Plus, Store } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { toast } from "sonner";
import { PageHeader } from "@/components/Layout";
import { Chip, EmptyState } from "@/components/Bits";
import { useBrand } from "@/context/BrandContext";
import api, { formatApiErrorDetail } from "@/lib/api";

const inputCls = "w-full bg-[#050505] border border-white/10 text-white rounded-sm px-3 py-2 text-sm focus:outline-none focus:border-white transition-colors";

export default function Brands() {
  const { brands, refreshBrands } = useBrand();
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: "", order_prefix: "", category: "", currency: "INR", website: "", email: "", phone: "" });

  const submit = async () => {
    if (!form.name || !form.order_prefix) return toast.error("Name and order prefix required");
    try { await api.post("/brands", form); toast.success("Brand created"); setOpen(false); setForm({ name: "", order_prefix: "", category: "", currency: "INR", website: "", email: "", phone: "" }); refreshBrands(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  return (
    <div>
      <PageHeader title="Brands" subtitle={`${brands.length} brands under Dasarro OS`}
        actions={<button data-testid="add-brand-button" onClick={() => setOpen(true)} className="flex items-center gap-2 text-sm bg-white text-black font-medium px-4 py-2 rounded-sm hover:bg-zinc-200 transition-colors"><Plus size={16} /> New Brand</button>} />
      <div className="p-8">
        {brands.length === 0 ? <EmptyState icon={Store} title="No brands" hint="Create your first brand." /> : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {brands.map((b) => (
              <div key={b.id} data-testid={`brand-card-${b.id}`} className="bg-[#121212] border border-white/10 rounded-sm p-5 hover:border-white/20 transition-colors">
                <div className="flex items-start justify-between">
                  <div><h3 className="font-display text-lg font-medium">{b.name}</h3><p className="text-xs text-zinc-500 mt-0.5">{b.category || "—"}</p></div>
                  <Chip className="bg-[#00FF66]/10 border-[#00FF66]/30 text-[#00FF66]">{b.status}</Chip>
                </div>
                <div className="mt-4 pt-3 border-t border-white/10 flex items-center justify-between text-xs">
                  <span className="font-mono text-zinc-500">{b.brand_id}</span>
                  <span className="font-mono text-[#4DA3FF]">Prefix: {b.order_prefix}</span>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-[#121212] border-white/10 text-white rounded-sm">
          <DialogHeader><DialogTitle className="font-display text-xl">New Brand</DialogTitle><DialogDescription className="sr-only">Create a brand with an order prefix.</DialogDescription></DialogHeader>
          <div className="space-y-3 pt-2">
            <div className="grid grid-cols-2 gap-3">
              <input data-testid="brand-name-input" className={inputCls} placeholder="Brand name *" value={form.name} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))} />
              <input data-testid="brand-prefix-input" className={inputCls} placeholder="Order prefix * (e.g. YP)" value={form.order_prefix} onChange={(e) => setForm((f) => ({ ...f, order_prefix: e.target.value.toUpperCase() }))} />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <input className={inputCls} placeholder="Category" value={form.category} onChange={(e) => setForm((f) => ({ ...f, category: e.target.value }))} />
              <input className={inputCls} placeholder="Currency" value={form.currency} onChange={(e) => setForm((f) => ({ ...f, currency: e.target.value }))} />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <input className={inputCls} placeholder="Email" value={form.email} onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))} />
              <input className={inputCls} placeholder="Phone" value={form.phone} onChange={(e) => setForm((f) => ({ ...f, phone: e.target.value }))} />
            </div>
            <button data-testid="brand-submit-button" onClick={submit} className="w-full text-sm bg-white text-black font-medium py-2.5 rounded-sm hover:bg-zinc-200 transition-colors">Create Brand</button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
